import React, { useState, useEffect } from "react";
import { HiViewfinderCircle } from "react-icons/hi2";
import * as turf from "@turf/turf";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  Polygon,
} from "@react-google-maps/api";
import { HiOutlineArrowLeft } from "react-icons/hi";
import {
  Camera,
  CameraResultType,
  CameraSource,
  CameraDirection,
} from "@capacitor/camera";
import { Geolocation } from "@capacitor/geolocation";
import { Capacitor } from "@capacitor/core";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import * as attendanceService from "../../Services/attendanceService";
import * as siteService from "../../Services/siteServices";
import { formatTimeAMPM } from "../../Utils/timeUtils";
import { Spinner } from "../../Components/Common";
import { ATTENDANCE_TYPES, MESSAGES } from "./constants";
import CameraPreviewComponent from "./Component/CameraPreviewComponent";
import { extractErrorMessage } from "../../Utils/extractErrorMessage";
import { parseWKTPolygon } from "../../Utils/geoUtils";
import { HOME } from "../../routes";

const validationSchema = Yup.object().shape({
  site_id: Yup.string().required(MESSAGES.SITES_NAME_REQUIRED),
  date: Yup.string().required(MESSAGES.DATE_REQUIRED),
  time: Yup.string().required(MESSAGES.TIME_REQUIRED),
});
const libraries = ["places"];

const AttendanceForm = ({ type }) => {
  const [photo, setPhoto] = useState(null);
  const [siteOptions, setSiteOptions] = useState([]);
  const [location, setLocation] = useState({ latitude: null, longitude: null });
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const [selectedSitePolygon, setSelectedSitePolygon] = useState(null);
  const [outsideGeofence, setOutsideGeofence] = useState(false);
  const isAndroid = Capacitor.getPlatform() === "android";
  const [loadingComplete, setLoadingComplete] = useState(false);
  const [siteLoaded, setSiteLoaded] = useState(false);
  const [locationLoaded, setLocationLoaded] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const containerStyle = { width: "100%", height: "300px" };
  const MAX_RETRIES = 3;

  const checkUserInsideGeofence = (coords) => {
    if (
      !coords ||
      coords.length < 3 ||
      !location.latitude ||
      !location.longitude
    ) {
      return false;
    }
    const userPoint = turf.point([location.longitude, location.latitude]);
    const sitePolygon = turf.polygon([coords]);
    const bufferedPolygon = turf.buffer(sitePolygon, 30, { units: "meters" });
    return turf.booleanPointInPolygon(userPoint, bufferedPolygon);
  };

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries,
  });

  const handleSiteChange = (e, form) => {
    const selectedSiteId = e.target.value;
    form.setFieldValue("site_id", selectedSiteId);

    const selectedSite = siteOptions.find(
      (site) =>
        site.id === selectedSiteId || site.id.toString() === selectedSiteId
    );

    if (selectedSite?.geofence) {
      const coords = parseWKTPolygon(selectedSite.geofence);
      setSelectedSitePolygon(coords);
      setOutsideGeofence(!checkUserInsideGeofence(coords));
    } else {
      setSelectedSitePolygon(null);
      setOutsideGeofence(false);
    }
  };

  const initialAttendanceValue = {
    site_id: "",
    date: new Date().toISOString().split("T")[0],
    time: new Date().toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
    }),
  };

  const takePhoto = async () => {
    try {
      const photo = await Camera.getPhoto({
        resultType: CameraResultType.Uri,
        source: CameraSource.Camera,
        quality: 50,
        cameraDirection: CameraDirection.Front,
        width: 640,
        height: 800,
        preserveAspectRatio: true,
      });

      const response = await fetch(photo.webPath);
      const blob = await response.blob();
      const file = new File([blob], "selfie.jpg", { type: blob.type });
      setPhoto(file);
    } catch (error) {
      toast.error(MESSAGES.CAMERA_ERROR, error);
    }
  };

  const getGeoLocation = async () => {
    try {
      const position = await Geolocation.getCurrentPosition({
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      });
      setLocation({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
      });
      setLocationLoaded(true);
    } catch (error) {
      toast.error(MESSAGES.LOCATION_DENIED_ERROR);
    }
  };

  const handleSubmit = async (values) => {
    if (submitting) return;
    setSubmitting(true);

    if (!photo) {
      setSubmitting(false);
      toast.error(MESSAGES.SELFIE_REQUIRED);
      return;
    }

    const formData = new FormData();
    formData.append("site", values.site_id);
    formData.append("date", values.date);
    formData.append("is_backdated", 0);
    formData.append("type", type);
    formData.append("time", values.time);
    formData.append("photo", photo);
    formData.append("latitude", location.latitude);
    formData.append("longitude", location.longitude);
    try {
      const response = await attendanceService.markAttenadance(formData);
      if (response.status === 201) {
        const checkinTime = response.data.checkin_time;
        toast.success(
          MESSAGES.SUBMITTED_ATTENDANCE(formatTimeAMPM(checkinTime))
        );
        navigate(-1);
      } else {
        const msg = extractErrorMessage(response) || MESSAGES.FAILED_SUBMISSION;
        toast.error(msg);
      }
    } catch (error) {
      const msg = extractErrorMessage(error) || MESSAGES.FAILED_SUBMISSION;
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (!selectedSitePolygon || selectedSitePolygon.length < 3) return;

    let cancelled = false;

    const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    const checkGeofence = async () => {
      let isInside = checkUserInsideGeofence(selectedSitePolygon);

      let retries = 0;
      while (!isInside && retries < MAX_RETRIES && !cancelled) {
        try {
          await sleep(3000);

          const position = await Geolocation.getCurrentPosition({
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0,
          });

          setLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });

          isInside = checkUserInsideGeofence(selectedSitePolygon);
        } catch (err) {
          break;
        }
        retries++;
      }

      if (!isInside && !cancelled) {
        alert(MESSAGES.NOT_IN_SITE_RETRY);
        navigate(HOME);
      }
    };

    checkGeofence();

    return () => {
      cancelled = true; // cleanup
    };
  }, [selectedSitePolygon]);

  useEffect(() => {
    const fetchSites = async () => {
      try {
        const response = await siteService.getSites({ sites: "my" });
        setSiteOptions(response.data.results);
        setSiteLoaded(true);
      } catch (error) {
        toast.error(MESSAGES.SITES_ERROR);
      }
    };
    fetchSites();
    getGeoLocation();
  }, []);

  useEffect(() => {
    if (isLoaded && siteLoaded && locationLoaded) {
      setLoadingComplete(true);
    }
  }, [isLoaded, siteLoaded, locationLoaded]);

  if (loadError) return <div>{MESSAGES.MAP_LOADING_ERROR}</div>;

  if (!loadingComplete) {
    return (
      <div className="h-screen w-full flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
  <>
    {submitting && (
      <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black bg-opacity-50">
        <Spinner />
      </div>
    )}

    <div className="w-full p-4 bg-white rounded-lg flex flex-col gap-2">
      {/* Header Section */}
      <div className="flex items-center justify-start gap-4 mb-2 md:mb-8 h-12 p-4">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="text-primary hover:text-secondary text-2xl"
          title="Back"
        >
          <HiOutlineArrowLeft />
        </button>
        <h2 className="text-2xl font-bold text-primary">
          {type === ATTENDANCE_TYPES.CHECKIN ? "Check-In" : "Check-Out"} Form
        </h2>
      </div>

      {/* Content Section */}
      <div className="flex flex-col lg:flex-row gap-8 flex-1 px-8">
        {/* Left Column - Form */}
        <div className="flex-1 flex flex-col">
          <Formik
            initialValues={initialAttendanceValue}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {() => (
              <Form className="space-y-6 text-secondary px-2 flex flex-col">
                {/* Site Selection */}
                <div>
                  <label className="block font-medium">Site</label>
                  <Field name="site_id">
                    {({ field, form }) => (
                      <select
                        {...field}
                        onChange={(e) => handleSiteChange(e, form)}
                        className="w-full border border-gray-300 rounded px-3 py-2 mt-1"
                      >
                        <option value="">Select a site</option>
                        {siteOptions.map((site) => (
                          <option key={site.id} value={site.id}>
                            {site.site_name}
                          </option>
                        ))}
                      </select>
                    )}
                  </Field>
                  <ErrorMessage
                    name="site_id"
                    component="div"
                    className="text-red-600 text-sm"
                  />
                  {outsideGeofence && (
                    <div className="text-red-600 text-sm mt-1">
                      You are not within the selected site's geofence.
                    </div>
                  )}
                </div>

                {/* Date & Time */}
                <div className="flex gap-4">
                  <div className="w-1/2">
                    <label className="block font-medium">Date</label>
                    <Field
                      name="date"
                      type="date"
                      disabled
                      className="w-full border border-gray-300 rounded px-3 py-2 mt-1 text-gray-500"
                    />
                    <ErrorMessage
                      name="date"
                      component="div"
                      className="text-red-600 text-sm"
                    />
                  </div>
                  <div className="w-1/2">
                    <label className="block font-medium">Time</label>
                    <Field
                      name="time"
                      type="time"
                      disabled
                      className="w-full border border-gray-300 rounded px-3 py-2 mt-1 text-gray-500"
                    />
                    <ErrorMessage
                      name="time"
                      component="div"
                      className="text-red-600 text-sm"
                    />
                  </div>
                </div>

                {/* Photo Section */}
                {!isAndroid && (
                  <div className="flex flex-col sm:flex-row sm:items-center sm:flex-wrap gap-2 py-2 w-full">
                    <div className="flex justify-center sm:justify-start items-center gap-4 w-full sm:w-auto">
                      <label className="font-medium whitespace-nowrap">
                        Upload Photo
                      </label>
                      <button
                        type="button"
                        onClick={takePhoto}
                        className="bg-primary text-white px-4 py-2 rounded hover:bg-secondary"
                        disabled={submitting}
                      >
                        Take Selfie
                      </button>
                    </div>

                    {photo && (
                      <div className="flex justify-center sm:justify-start items-center gap-4 w-full sm:w-auto">
                        <button
                          type="button"
                          className="text-primary underline flex items-center gap-1"
                          onClick={() => setShowPreview(true)}
                        >
                          <HiViewfinderCircle className="shrink-0" />
                          <span>View Selfie</span>
                        </button>
                        <p className="text-green-600 whitespace-nowrap">
                          Selfie captured ✔
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {isAndroid && (
                  <div className="flex justify-between gap-2">
                    <label className="block font-medium py-2">
                      Upload Photo
                    </label>
                    <CameraPreviewComponent onPhotoCapture={setPhoto} />
                  </div>
                )}

                {/* Footer Buttons */}
                <div className="mt-auto flex justify-end pt-8 gap-6 border-t">
                  <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="bg-primary text-white px-4 py-2 rounded hover:bg-secondary"
                    disabled={submitting || outsideGeofence}
                  >
                    {submitting ? "Submitting..." : "Submit"}
                  </button>
                </div>
                
              </Form>
            )}
          </Formik>
        </div>

        {/* Right Column — Map */}
        {isLoaded && location.latitude && location.longitude && (
          <div className="flex-1 flex items-center justify-center">
            <div className="w-full h-[40vh] md:h-[60vh] rounded-lg overflow-hidden border">
              <GoogleMap
                mapContainerStyle={{ width: "100%", height: "100%" }}
                center={{
                  lat: location.latitude,
                  lng: location.longitude,
                }}
                zoom={14}
                options={{
                  gestureHandling: "none",
                  draggable: false,
                  scrollwheel: false,
                  disableDoubleClickZoom: true,
                  keyboardShortcuts: false,
                }}
              >
                <Marker
                  position={{
                    lat: location.latitude,
                    lng: location.longitude,
                  }}
                />
                {selectedSitePolygon?.length > 0 && (
                  <Polygon
                    paths={selectedSitePolygon.map(([lng, lat]) => ({
                      lat,
                      lng,
                    }))}
                    options={{
                      fillColor: "#00BFFF",
                      fillOpacity: 0.3,
                      strokeColor: "#1E90FF",
                      strokeOpacity: 0.8,
                      strokeWeight: 2,
                    }}
                  />
                )}
              </GoogleMap>
            </div>
          </div>
        )}
      </div>
      
    </div>

    {/* Selfie Preview Modal */}
    {showPreview && photo && (
      <div className="fixed inset-0 z-[9999] bg-black bg-opacity-60 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg w-full max-w-lg mx-4 sm:mx-6 max-h-[90vh] overflow-auto relative">
          <div className="flex justify-between items-center px-4 py-2 border-b">
            <h3 className="text-lg font-semibold text-primary">
              Selfie Preview
            </h3>
            <button
              onClick={() => setShowPreview(false)}
              className="text-red-500 font-bold text-xl hover:text-red-700"
            >
              &times;
            </button>
          </div>
          <div className="p-4 flex justify-center">
            <img
              src={URL.createObjectURL(photo)}
              alt="Selfie Preview"
              className="max-w-full max-h-[70vh] object-contain rounded border"
            />
          </div>
        </div>
      </div>
    )}
  </>
);

};

export default AttendanceForm;
